import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import { Container, Row, Col, Card, Button, Form, ProgressBar, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  FaInstagram,
  FaGlobe,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaChild,
  FaImages,
  FaFileAlt,
  FaPalette,
  FaCheckCircle,
  FaArrowLeft,
  FaArrowRight,
  FaTimes,
  FaUser,
  FaGripVertical,
  FaPlusCircle,
  FaSpinner
} from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { BackgroundService, PostService, FotoarchivService } from '../Services';
import ApiService from '../Services/ApiService';
import './Wizard.css';
import {ImSpinner8} from "react-icons/im";
import {useAuth} from "../Context/AuthContext";


const Wizard = () => {
  const { account, backendToken } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [backgroundOptions, setBackgroundOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [variantOptions, setVariantOptions] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [createdPost, setCreatedPost] = useState(null); // Speichert Response mit generatedImages
  const prevFormDataRef = useRef(null); // Trackt vorherige formData
  const [lightboxImage, setLightboxImage] = useState(null); // Für Bildvorschau
  const [lightboxIndex, setLightboxIndex] = useState(0); // Index des aktuellen Bildes
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false); // Erfolgs-Overlay
  const [uploadedImagesStatus, setUploadedImagesStatus] = useState({}); // Track welche Bilder hochgeladen sind
  const [fotoarchivThumbnails, setFotoarchivThumbnails] = useState({}); // Cache für Thumbnails
  const [generatedImageBlobUrls, setGeneratedImageBlobUrls] = useState({}); // Blob-URLs für generierte Bilder (mit Auth)

  // Letzte Beiträge (Step 1)
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentPostCovers, setRecentPostCovers] = useState({}); // _id → blob URL (page 1)
  const [selectedRecentPost, setSelectedRecentPost] = useState(null); // geöffnetes Detail-Modal
  const [selectedPostAllImages, setSelectedPostAllImages] = useState({}); // page → blob URL
  const [postDetailLightbox, setPostDetailLightbox] = useState(null); // blob URL im Lightbox
  const [postDetailLightboxIndex, setPostDetailLightboxIndex] = useState(0);

  // Letzte Fotoarchive (Step 1)
  const [recentFotoarchive, setRecentFotoarchive] = useState([]);
  const [recentFotoarchivCovers, setRecentFotoarchivCovers] = useState({}); // _id → blob URL

  // Hilfsfunktion: Erstellt ein Thumbnail für ein Bild
  const createThumbnail = useCallback((file, maxWidth = 300, maxHeight = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Berechne neue Dimensionen unter Beibehaltung des Seitenverhältnisses
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Konvertiere zu Blob mit reduzierter Qualität
          canvas.toBlob(
            (blob) => {
              resolve(URL.createObjectURL(blob));
            },
            'image/jpeg',
            0.7 // 70% Qualität für Thumbnails
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Hilfsfunktion: Konvertiert Datum von YYYY-MM-DD zu TT.MM.JJJJ
  const formatDateForBackend = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}.${month}.${year}`;
  };

  const [formData, setFormData] = useState({
    platforms: [],
    title: '',
    date: new Date().toISOString().split('T')[0], // Format: YYYY-MM-DD für input type="date"
    location: '',
    leaders: 0,
    children: 0,
    images: [],
    homepageImages: [],
    description: '',
    homepageDescription: '',
    backgroundImage: null,
    // Fotoarchiv-spezifische Felder
    fotoarchivName: '',
    fotoarchivDatum: new Date().toISOString().split('T')[0],
    fotoarchivOrt: '',
    fotoarchivImages: [],
    fotoarchivAktionId: null
  });

  useEffect(() => {
    console.log("DATA:::", formData);
  }, [formData]);

  // Setze createdPost zurück wenn sich formData ändert (inkl. backgroundImage/Design)
  useEffect(() => {
    // Erstelle einen String-Key aus den relevanten formData-Feldern
    const currentFormDataKey = JSON.stringify({
      platforms: formData.platforms,
      title: formData.title,
      date: formData.date,
      location: formData.location,
      leaders: formData.leaders,
      children: formData.children,
      images: formData.images.map(f => f.name + f.size), // Nur Name und Größe für Vergleich
      homepageImages: formData.homepageImages.map(f => f.name + f.size),
      description: formData.description,
      homepageDescription: formData.homepageDescription,
      overlayText: formData.overlayText,
      backgroundImage: formData.backgroundImage?.id ?? formData.backgroundImage ?? null,
    });

    // Vergleiche mit vorherigem Wert
    if (prevFormDataRef.current && prevFormDataRef.current !== currentFormDataKey && createdPost) {
      console.log('🔄 formData hat sich geändert - setze createdPost zurück');
      setCreatedPost(null);
    }

    // Aktualisiere Ref
    prevFormDataRef.current = currentFormDataKey;
  }, [
    formData.platforms,
    formData.title,
    formData.date,
    formData.location,
    formData.leaders,
    formData.children,
    formData.images,
    formData.homepageImages,
    formData.description,
    formData.homepageDescription,
    formData.overlayText,
    formData.backgroundImage,
    createdPost
  ]);

  // Wenn createdPost gesetzt wird, lade alle generierten Bilder mit Bearer Token als Blob-URLs
  useEffect(() => {
    if (!createdPost?.generatedImages?.length) {
      setGeneratedImageBlobUrls({});
      return;
    }
    let cancelled = false;
    const fetchBlobs = async () => {
      const urls = {};
      await Promise.all(
        createdPost.generatedImages.map(async (image) => {
          try {
            const blobUrl = await ApiService.fetchBlobUrl(`/events/${createdPost.id}/images/${image.page}`);
            if (!cancelled) urls[image.page] = blobUrl;
          } catch (e) {
            console.error(`❌ Fehler beim Laden des Bildes (Seite ${image.page}):`, e);
          }
        })
      );
      if (!cancelled) setGeneratedImageBlobUrls(urls);
    };
    fetchBlobs();
    return () => { cancelled = true; };
  }, [createdPost]);

  // Lade letzte Instagram-Beiträge für Step 1
  useEffect(() => {
    if (!backendToken) return;
    let cancelled = false;
    const loadRecentPosts = async () => {
      try {
        const events = await PostService.getEvents();
        const instagramPosts = events
          .filter(e => e.platforms && e.platforms.includes('instagram') && e.generatedImages && e.generatedImages.length > 0)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 3);
        if (!cancelled) setRecentPosts(instagramPosts);
      } catch (e) {
        console.error('❌ Fehler beim Laden der letzten Beiträge:', e);
      }
    };
    loadRecentPosts();
    return () => { cancelled = true; };
  }, [backendToken]);

  // Lade Cover-Bilder (page 1) der letzten Beiträge
  useEffect(() => {
    if (!recentPosts.length || !backendToken) return;
    let cancelled = false;
    const fetchCovers = async () => {
      const covers = {};
      await Promise.all(
        recentPosts.map(async (post) => {
          const page1 = post.generatedImages.find(img => img.page === 1);
          if (!page1) return;
          try {
            const blobUrl = await ApiService.fetchBlobUrl(`/events/${post._id}/images/1`);
            if (!cancelled) covers[post._id] = blobUrl;
          } catch (e) {
            console.error(`❌ Cover-Bild für Event ${post._id} konnte nicht geladen werden:`, e);
          }
        })
      );
      if (!cancelled) setRecentPostCovers(prev => ({ ...prev, ...covers }));
    };
    fetchCovers();
    return () => { cancelled = true; };
  }, [recentPosts, backendToken]);

  // Lade letzte Fotoarchive für Step 1
  useEffect(() => {
    if (!backendToken) return;
    let cancelled = false;
    const loadFotoarchive = async () => {
      try {
        const aktionen = await FotoarchivService.getAktionen();
        console.log('📂 Fotoarchive geladen:', aktionen);
        const recent = [...aktionen]
          .sort((a, b) => {
            const dateA = new Date(b.createdAt || b.datum || 0);
            const dateB = new Date(a.createdAt || a.datum || 0);
            return dateA - dateB;
          })
          .slice(0, 2)
          .map(a => ({ ...a, _id: a._id || a.id })); // Normalisiere ID-Feld
        console.log('📂 Letzte 2 Fotoarchive:', recent);
        if (!cancelled) setRecentFotoarchive(recent);
      } catch (e) {
        console.error('❌ Fehler beim Laden der letzten Fotoarchive:', e);
      }
    };
    loadFotoarchive();
    return () => { cancelled = true; };
  }, [backendToken]);

  // Lade Cover-Bilder der letzten Fotoarchive (erstes Foto der Aktion)
  useEffect(() => {
    if (!recentFotoarchive.length || !backendToken) return;
    let cancelled = false;
    const fetchFotoarchivCovers = async () => {
      const covers = {};
      await Promise.all(
        recentFotoarchive.map(async (aktion) => {
          const id = aktion._id || aktion.id;
          if (!id) return;
          const firstFoto = aktion.fotos?.[0];
          if (!firstFoto) return;
          try {
            const blobUrl = await ApiService.fetchBlobUrl(`/fotoarchiv/${id}/foto/${firstFoto.filename}`);
            if (!cancelled) covers[id] = blobUrl;
          } catch (e) {
            console.error(`❌ Cover für Fotoarchiv ${id} nicht geladen:`, e);
          }
        })
      );
      if (!cancelled) setRecentFotoarchivCovers(prev => ({ ...prev, ...covers }));
    };
    fetchFotoarchivCovers();
    return () => { cancelled = true; };
  }, [recentFotoarchive, backendToken]);

  // Lade alle generierten Bilder wenn ein Beitrag im Detail angezeigt wird
  useEffect(() => {
    if (!selectedRecentPost) {
      setSelectedPostAllImages({});
      return;
    }
    let cancelled = false;
    const fetchDetailImages = async () => {
      const urls = {};
      await Promise.all(
        (selectedRecentPost.generatedImages || []).map(async (img) => {
          try {
            const blobUrl = await ApiService.fetchBlobUrl(`/events/${selectedRecentPost._id}/images/${img.page}`);
            if (!cancelled) urls[img.page] = blobUrl;
          } catch (e) {
            console.error(`❌ Detailbild Seite ${img.page} konnte nicht geladen werden:`, e);
          }
        })
      );
      if (!cancelled) setSelectedPostAllImages(urls);
    };
    fetchDetailImages();
    return () => { cancelled = true; };
  }, [selectedRecentPost]);

  // Lade verfügbare Farben beim Component Mount (oder wenn Token wechselt)
  React.useEffect(() => {
    if (!backendToken) {
      console.warn('🎨 loadColors: kein backendToken – überspringe');
      return;
    }
    console.log('🎨 loadColors: starte Farbenladen, Token vorhanden:', !!backendToken);
    let cancelled = false;
    const loadColors = async () => {
      try {
        console.log('🎨 loadColors: rufe BackgroundService.getColors() auf...');
        const colors = await BackgroundService.getColors();
        console.log('🎨 loadColors: Antwort von BackgroundService.getColors():', colors);
        console.log('🎨 loadColors: Anzahl Farben:', colors?.length);
        // Nur überschreiben wenn tatsächlich Farben zurückkamen – nie mit leerem Array löschen
        if (!cancelled && colors.length > 0) {
          setColorOptions(colors);
          console.log('🎨 loadColors: colorOptions gesetzt mit', colors.length, 'Farbe(n)');
        } else if (colors.length === 0) {
          console.warn('🎨 loadColors: Backend hat leeres Array zurückgegeben – colorOptions NICHT überschrieben');
        }
      } catch (error) {
        console.error('🎨 loadColors: Fehler beim Laden der Farben:', error);
      }
    };

    loadColors();
    return () => { cancelled = true; };
  }, [backendToken]);

  // Lade Varianten wenn eine Farbe ausgewählt wird
  React.useEffect(() => {
    if (!backendToken || !selectedColor) return;
    let cancelled = false;
    const loadVariants = async () => {
      try {
        const variants = await BackgroundService.getVariants(selectedColor);
        if (!cancelled) setVariantOptions(variants);
      } catch (error) {
        console.error('Fehler beim Laden der Varianten:', error);
      }
    };

    loadVariants();
    return () => { cancelled = true; };
  }, [selectedColor, backendToken]);

  // Wenn zum Design-Step navigiert wird: selectedColor zurücksetzen
  // damit immer die Farbauswahl (nicht Varianten) angezeigt wird
  React.useEffect(() => {
    const currentStepData = steps[currentStep - 1];
    if (currentStepData?.type === 'color') {
      setSelectedColor(null);
      setVariantOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Dynamische Schritte basierend auf ausgewählten Plattformen
  const getSteps = () => {
    const baseSteps = [
      { number: 1, title: 'Plattform', icon: FaGlobe }
    ];

    // Zeige Details nur wenn mindestens eine Plattform ausgewählt ist
    if (formData.platforms.length > 0) {
      baseSteps.push(
        { number: 2, title: 'Details', icon: FaCalendarAlt }
      );
    }

    let stepNumber = 3;

    if (formData.platforms.includes('instagram')) {
      baseSteps.push(
        { number: stepNumber++, title: 'Instagram', icon: FaInstagram, type: 'instagram' }
      );
    }

    if (formData.platforms.includes('fotoarchiv')) {
      baseSteps.push(
        { number: stepNumber++, title: 'Fotos', icon: FaImages, type: 'fotoarchiv' }
      );
    }

    if (formData.platforms.includes('homepage')) {
      baseSteps.push(
        { number: stepNumber++, title: 'Homepage', icon: FaGlobe, type: 'homepage' }
      );
    }

    // Zeige Farbe nur wenn Instagram ausgewählt ist
    if (formData.platforms.includes('instagram')) {
      baseSteps.push(
        { number: stepNumber++, title: 'Design', icon: FaPalette, type: 'color' }
      );

      // Preview-Step nur anzeigen wenn ein Hintergrund ausgewählt wurde
      if (formData.backgroundImage) {
        baseSteps.push(
          { number: stepNumber++, title: 'Vorschau', icon: FaFileAlt, type: 'preview' }
        );
      }
    }

    // Zeige Erstellen nur wenn mindestens eine Plattform ausgewählt ist
    if (formData.platforms.length > 0) {
      baseSteps.push(
        { number: stepNumber++, title: 'Erstellen', icon: FaCheckCircle, type: 'submit' }
      );
    }

    return baseSteps;
  };

  const steps = getSteps();
  const totalSteps = steps.length;
  const progressPercentage = (currentStep / totalSteps) * 100;

  const handleNext = async () => {
    console.log('➡️ handleNext aufgerufen:', {
      currentStep,
      totalSteps,
      nextStep: currentStep + 1,
      hasCreatedPost: !!createdPost
    });

    // Wenn Fotoarchiv und Details-Step (Step 2), erstelle Aktion
    if (currentStep === 2 && formData.platforms.includes('fotoarchiv') && formData.platforms.length === 1) {
      try {
        console.log('📤 Erstelle Fotoarchiv-Aktion...');
        const result = await FotoarchivService.createAktion({
          name: formData.fotoarchivName,
          datum: formatDateForBackend(formData.fotoarchivDatum),
          ort: formData.fotoarchivOrt,
          ersteller: account?.username || '' // Füge Ersteller hinzu
        });

        console.log('✅ Fotoarchiv-Aktion erstellt:', result);
        setFormData(prev => ({
          ...prev,
          fotoarchivAktionId: result.aktion.id
        }));
      } catch (error) {
        console.error('❌ Fehler beim Erstellen der Fotoarchiv-Aktion:', error);
        alert('Fehler beim Erstellen der Aktion. Bitte versuche es erneut.');
        return;
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      console.log('✅ Step gewechselt zu:', currentStep + 1);
    } else {
      console.log('⚠️ Bereits beim letzten Step');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // Wenn wir zu Step 1 (Plattformauswahl) zurückgehen, setze alles zurück
      if (currentStep === 2) {
        // Cleanup Thumbnails
        Object.values(fotoarchivThumbnails).forEach(url => {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            // Ignore errors
          }
        });
        setFotoarchivThumbnails({});

        setFormData({
          platforms: [],
          title: '',
          date: new Date().toISOString().split('T')[0],
          location: '',
          leaders: 0,
          children: 0,
          images: [],
          homepageImages: [],
          description: '',
          homepageDescription: '',
          backgroundImage: null,
          fotoarchivName: '',
          fotoarchivDatum: new Date().toISOString().split('T')[0],
          fotoarchivOrt: '',
          fotoarchivImages: [],
          fotoarchivAktionId: null
        });
        setCreatedPost(null);
        setSelectedColor(null);
        setVariantOptions([]);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepNumber) => {
    // Verhindere Navigation zu Steps die noch nicht verfügbar sind
    if (stepNumber <= currentStep || (stepNumber === 2 && formData.platforms.length > 0)) {
      setCurrentStep(stepNumber);
    }
  };

  const handlePlatformToggle = (platform) => {
    // Nur eine Plattform gleichzeitig - beim Wechsel alles zurücksetzen
    if (!formData.platforms.includes(platform)) {
      // Cleanup Thumbnails
      Object.values(fotoarchivThumbnails).forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      });
      setFotoarchivThumbnails({});

      setFormData({
        platforms: [platform], // Nur die neue Plattform
        title: '',
        date: new Date().toISOString().split('T')[0],
        location: '',
        leaders: 0,
        children: 0,
        images: [],
        homepageImages: [],
        description: '',
        homepageDescription: '',
        backgroundImage: null,
        fotoarchivName: '',
        fotoarchivDatum: new Date().toISOString().split('T')[0],
        fotoarchivOrt: '',
        fotoarchivImages: [],
        fotoarchivAktionId: null
      });

      // Setze auch andere States zurück
      setCreatedPost(null);
      setSelectedColor(null);
      setVariantOptions([]);

      setCurrentStep(2);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...acceptedFiles]
    }));
  }, []);

  // Memoize Bild-URLs um URL.createObjectURL() nicht bei jedem Render aufzurufen
  const imagePreviewData = useMemo(() => {
    const urlMap = new Map();
    formData.images.forEach((file, index) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (!urlMap.has(key)) {
        urlMap.set(key, {
          url: URL.createObjectURL(file),
          key: key,
          file: file,
          index: index
        });
      }
    });
    return Array.from(urlMap.values());
  }, [formData.images]);

  // Cleanup URLs when component unmounts
  useEffect(() => {
    return () => {
      imagePreviewData.forEach(({ url }) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      });
      // Cleanup Fotoarchiv Thumbnails
      Object.values(fotoarchivThumbnails).forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      });
    };
  }, [imagePreviewData, fotoarchivThumbnails]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: true
  });

  const removeImage = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  }, []);

  // Homepage-spezifische Dropzone-Handlers
  const onHomepageImagesDrop = (acceptedFiles) => {
    setFormData(prev => ({
      ...prev,
      homepageImages: [...prev.homepageImages, ...acceptedFiles]
    }));
  };

  const { getRootProps: getHomepageRootProps, getInputProps: getHomepageInputProps, isDragActive: isHomepageDragActive } = useDropzone({
    onDrop: onHomepageImagesDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: true
  });

  const removeHomepageImage = (index) => {
    setFormData(prev => ({
      ...prev,
      homepageImages: prev.homepageImages.filter((_, i) => i !== index)
    }));
  };

  // Fotoarchiv-spezifische Dropzone-Handlers
  const onFotoarchivDrop = useCallback(async (acceptedFiles) => {
    setFormData(prev => ({
      ...prev,
      fotoarchivImages: [...prev.fotoarchivImages, ...acceptedFiles]
    }));

    // Erstelle Thumbnails asynchron im Hintergrund
    const thumbnailPromises = acceptedFiles.map(async (file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      try {
        const thumbnailUrl = await createThumbnail(file);
        setFotoarchivThumbnails(prev => ({
          ...prev,
          [key]: thumbnailUrl
        }));
      } catch (error) {
        console.error('Fehler beim Erstellen des Thumbnails:', error);
        // Fallback: Verwende Original-Bild
        setFotoarchivThumbnails(prev => ({
          ...prev,
          [key]: URL.createObjectURL(file)
        }));
      }
    });

    // Warte nicht auf die Thumbnails - sie werden asynchron geladen
    Promise.all(thumbnailPromises).catch(console.error);
  }, [createThumbnail]);

  const { getRootProps: getFotoarchivRootProps, getInputProps: getFotoarchivInputProps, isDragActive: isFotoarchivDragActive } = useDropzone({
    onDrop: onFotoarchivDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: true
  });

  const removeFotoarchivImage = (index) => {
    const file = formData.fotoarchivImages[index];
    const uploadKey = `${file.name}-${file.size}-${file.lastModified}`;

    // Cleanup Thumbnail URL
    if (fotoarchivThumbnails[uploadKey]) {
      try {
        URL.revokeObjectURL(fotoarchivThumbnails[uploadKey]);
      } catch (e) {
        // Ignore errors
      }
      setFotoarchivThumbnails(prev => {
        const newThumbs = { ...prev };
        delete newThumbs[uploadKey];
        return newThumbs;
      });
    }

    setFormData(prev => ({
      ...prev,
      fotoarchivImages: prev.fotoarchivImages.filter((_, i) => i !== index)
    }));
  };

  // Drag & Drop Handler für Instagram Bilder
  const handleInstagramDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formData.images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormData(prev => ({
      ...prev,
      images: items
    }));
  };

  // Drag & Drop Handler für Homepage Bilder
  const handleHomepageDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formData.homepageImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormData(prev => ({
      ...prev,
      homepageImages: items
    }));
  };

  // Post erstellen
  const handleSubmitPost = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Finde das ausgewählte Hintergrundbild
      const selectedBackground = variantOptions.find(v => v.id === formData.backgroundImage);
      console.log('🎨 Background Debug:');
      console.log('  formData.backgroundImage:', formData.backgroundImage);
      console.log('  selectedBackground:', selectedBackground);
      console.log('  selectedBackground.path:', selectedBackground?.path);

      // Bereite die Daten für PostService vor
      const postData = {
        platforms: formData.platforms,
        title: formData.title,
        date: formatDateForBackend(formData.date), // Konvertiere zu TT.MM.JJJJ
        location: formData.location,
        leaders: formData.leaders,
        children: formData.children,
        description: formData.description || '',
        homepageDescription: formData.homepageDescription || '',
        images: formData.images,
        homepageImages: formData.homepageImages,
        backgroundImage: selectedBackground ? selectedBackground.path : null,
        ersteller: account?.username || '' // Füge Ersteller hinzu
      };

      console.log('📤 Sende postData:', postData);

      // Sende an Backend
      const response = await PostService.createPost(postData);

      console.log('Post erfolgreich erstellt:', response);
      setSubmitSuccess(true);

      // Gehe zum nächsten Step (Zusammenfassung)
      setTimeout(() => {
        handleNext();
      }, 1500);

    } catch (error) {
      console.error('Fehler beim Erstellen des Posts:', error);
      setSubmitError(error.message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 }
  };

  const renderStepContent = () => {
    const currentStepData = steps[currentStep - 1];
    /*console.log('🔍 renderStepContent aufgerufen:', {
      currentStep,
      currentStepData,
      hasCreatedPost: !!createdPost,
      hasGeneratedImages: !!createdPost?.generatedImages,
      generatedImagesCount: createdPost?.generatedImages?.length
    });*/

    switch (currentStepData?.type || currentStep) {
      case 1:
        return (
            <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fullscreen-step"
            >
              <div className="step-header">
                <h2>Was möchtest du machen?</h2>
              </div>

              <div className="step1-layout">
                {/* Linke Spalte: Plattformauswahl */}
                <div className="step1-left">
                  <div className="platform-selection">
                    <div
                        className={`platform-option ${formData.platforms.includes('instagram') ? 'selected' : ''}`}
                        onClick={() => handlePlatformToggle('instagram')}
                    >
                      <div className="platform-icon instagram">
                        <FaInstagram size={40} />
                      </div>
                      <div className="platform-info">
                        <h4>Instagram</h4>
                        <p>Feed Post</p>
                      </div>
                      <div className="platform-check">
                        {formData.platforms.includes('instagram') && <FaCheckCircle />}
                      </div>
                    </div>

                    <div
                        className={`platform-option disabled`}
                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <div className="platform-icon homepage">
                        <FaGlobe size={40} />
                      </div>
                      <div className="platform-info">
                        <h4>Homepage</h4>
                        <p>Bald verfügbar</p>
                      </div>
                    </div>

                    <div
                        className={`platform-option ${formData.platforms.includes('fotoarchiv') ? 'selected' : ''}`}
                        onClick={() => handlePlatformToggle('fotoarchiv')}
                    >
                      <div className="platform-icon fotoarchiv">
                        <FaImages size={40} />
                      </div>
                      <div className="platform-info">
                        <h4>Fotoarchiv</h4>
                        <p>Fotos hochladen</p>
                      </div>
                      <div className="platform-check">
                        {formData.platforms.includes('fotoarchiv') && <FaCheckCircle />}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rechte Spalte: Letzte Beiträge & Fotoarchive */}
                {(recentPosts.length > 0 || recentFotoarchive.length > 0) && (
                  <div className="step1-right">
                    {/* Letzte Instagram-Beiträge */}
                    {recentPosts.length > 0 && (
                      <div className="recent-posts-section">
                        <h3 className="recent-posts-title">Meine letzten Beiträge:</h3>
                        <div className="recent-posts-grid">
                          {recentPosts.map(post => (
                            <div
                              key={post._id}
                              className="recent-post-card"
                              onClick={() => setSelectedRecentPost(post)}
                            >
                              <div className="recent-post-cover-wrap">
                                {recentPostCovers[post._id] ? (
                                  <img
                                    src={recentPostCovers[post._id]}
                                    alt={post.title}
                                    className="recent-post-cover"
                                  />
                                ) : (
                                  <div className="recent-post-cover-placeholder">
                                    <ImSpinner8 className="spin" size={24} />
                                  </div>
                                )}
                              </div>
                              <div className="recent-post-meta">
                                <p className="recent-post-card-title">{post.title}</p>
                                <p className="recent-post-card-date">{post.date}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Letzte Fotoarchive */}
                    {recentFotoarchive.length > 0 && (
                      <div className="recent-posts-section">
                        <h3 className="recent-posts-title">Meine letzten Fotoarchive:</h3>
                        <div className="recent-fotoarchiv-grid">
                          {recentFotoarchive.map(aktion => {
                            const id = aktion._id || aktion.id;
                            return (
                              <div
                                key={id}
                                className="recent-post-card recent-fotoarchiv-card"
                              >
                                <div className="recent-post-cover-wrap">
                                  {recentFotoarchivCovers[id] ? (
                                    <img
                                      src={recentFotoarchivCovers[id]}
                                      alt={aktion.name}
                                      className="recent-post-cover"
                                    />
                                  ) : (
                                    <div className="recent-post-cover-placeholder">
                                      <FaImages size={28} color="#555" />
                                    </div>
                                  )}
                                </div>
                                <div className="recent-post-meta">
                                  <p className="recent-post-card-title">{aktion.name}</p>
                                  <p className="recent-post-card-date">{aktion.datum}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
        );

      case 2:
        return (
            <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fullscreen-step"
            >
              <div className="step-header">
                <h2>Details</h2>
                <p>Erzähle uns von deiner Aktion</p>
              </div>

              <div className="form-container">
                <div className="input-group">
                  <label><FaFileAlt /> {formData.platforms.includes('fotoarchiv') ? 'Name der Aktion' : 'Beitragstitel'}</label>
                  <input
                      type="text"
                      placeholder="Sommerlager CaEx"
                      value={formData.platforms.includes('fotoarchiv') ? formData.fotoarchivName : formData.title}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        ...(formData.platforms.includes('fotoarchiv')
                          ? { fotoarchivName: e.target.value }
                          : { title: e.target.value })
                      }))}
                      className="mobile-input"
                  />
                </div>

                <div className="input-group">
                  <label><FaCalendarAlt /> Datum</label>
                  <input
                      type="date"
                      value={formData.platforms.includes('fotoarchiv') ? formData.fotoarchivDatum : formData.date}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        ...(formData.platforms.includes('fotoarchiv')
                          ? { fotoarchivDatum: e.target.value }
                          : { date: e.target.value })
                      }))}
                      className="mobile-input date-input"
                  />
                </div>

                <div className="input-group">
                  <label><FaMapMarkerAlt /> Ort</label>
                  <input
                      type="text"
                      placeholder="Damüls"
                      value={formData.platforms.includes('fotoarchiv') ? formData.fotoarchivOrt : formData.location}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        ...(formData.platforms.includes('fotoarchiv')
                          ? { fotoarchivOrt: e.target.value }
                          : { location: e.target.value })
                      }))}
                      className="mobile-input"
                  />
                </div>

                {/* Leiter und Kinder nur für Instagram/Homepage */}
                {!formData.platforms.includes('fotoarchiv') && (
                  <div className="number-inputs">
                    <div className="input-group">
                      <label><FaUsers /> Leiter</label>
                      <input
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          value={formData.leaders}
                          onChange={(e) => setFormData(prev => ({ ...prev, leaders: parseInt(e.target.value) || 0 }))}
                          onFocus={(e) => e.target.select()}
                          className="mobile-input number-input"
                      />
                    </div>

                    <div className="input-group">
                      <label><FaChild /> Kinder</label>
                      <input
                          type="number"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          value={formData.children}
                          onChange={(e) => setFormData(prev => ({ ...prev, children: parseInt(e.target.value) || 0 }))}
                          onFocus={(e) => e.target.select()}
                          className="mobile-input number-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
        );

      case 'instagram':
        return (
            <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fullscreen-step"
            >
              <div className="step-header">
                <h2>Instagram Inhalte</h2>
                <p>Füge Bilder und Texte für Instagram hinzu</p>
              </div>

              <div className="instagram-content">
                <div className="upload-container">


                  {formData.images.length > 0 ? (
                      <DragDropContext onDragEnd={handleInstagramDragEnd}>
                        <Droppable droppableId="instagram-images" direction="horizontal">
                          {(provided) => (
                              <div
                                  className="image-grid mb-3"
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                              >
                                {formData.images.map((file, index) => {
                                  const stableKey = `${file.name}-${file.size}-${file.lastModified}`;
                                  const previewData = imagePreviewData.find(p => p.key === stableKey);
                                  const imageUrl = previewData ? previewData.url : URL.createObjectURL(file);

                                  return (
                                    <Draggable key={stableKey} draggableId={stableKey} index={index}>
                                      {(provided, snapshot) => (
                                          <div
                                              className={`image-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                          >
                                            <img
                                                src={imageUrl}
                                                alt={`Instagram ${index + 1}`}
                                                draggable={false}
                                            />
                                            <button
                                                className="remove-image"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  removeImage(index);
                                                }}
                                            >
                                              <FaTimes fill="white" />
                                            </button>
                                          </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                <div
                                    className={`add-additional-images `}
                                    {...getRootProps()}
                                >
                                  <input {...getInputProps()} />
                                  <FaPlusCircle size={60} />
                                </div>
                                {provided.placeholder}
                              </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                  ) : (
                      <div
                          {...getRootProps()}
                          className={`mobile-dropzone instagram-dropzone ${isDragActive ? 'active' : ''}`}
                      >
                        <input {...getInputProps()} />
                        <FaInstagram size={60} />
                        <h3>Instagram Bilder</h3>
                        <p>hochwertig, max. 10 Bilder</p>
                      </div>
                  )}

                </div>

                <div className="text-container">
                <textarea
                    className="mobile-textarea instagram-textarea"
                    placeholder="🎉 Was für ein toller Tag!

Unser Sommerlager war ein voller Erfolg! Danke an alle, die dabei waren! 💫

#PfadiDornbirn #Sommerlager #Spaß #Gemeinschaft"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={10}
                />
                  <div className="character-count instagram-count">
                    {formData.description.length} / 2200 Zeichen
                  </div>
                  <div className="instagram-tips">
                    💡 Tipp: Nutze Emojis und max. 30 Hashtags für mehr Reichweite
                  </div>
                </div>
              </div>
            </motion.div>
        );

      case 'homepage':
        return (
            <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fullscreen-step"
            >
              <div className="step-header">
                <h2>Homepage Inhalte</h2>
                <p>Füge Bilder und Texte für die Homepage hinzu</p>
              </div>

              <div className="homepage-content">
                <div className="upload-container">
                  <div
                      {...getHomepageRootProps()}
                      className={`mobile-dropzone homepage-dropzone ${isHomepageDragActive ? 'active' : ''}`}
                  >
                    <input {...getHomepageInputProps()} />
                    <FaGlobe size={60} />
                    <h3>Homepage Bilder</h3>
                    <p>Hochauflösend, verschiedene Formate möglich</p>
                  </div>

                  {formData.homepageImages.length > 0 && (
                      <DragDropContext onDragEnd={handleHomepageDragEnd}>
                        <Droppable droppableId="homepage-images">
                          {(provided) => (
                            <div
                              className="image-grid mb-3"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {formData.homepageImages.map((file, index) => {
                                // Erstelle einen stabilen Key basierend auf Datei-Eigenschaften
                                const stableKey = `homepage-${file.name}-${file.size}-${file.lastModified}`;
                                return (
                                  <Draggable key={stableKey} draggableId={stableKey} index={index}>
                                    {(provided, snapshot) => (
                                      <div
                                        className={`image-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                        ref={provided.innerRef}
                                      >
                                        <img
                                          src={URL.createObjectURL(file)}
                                          alt={`Homepage ${index + 1}`}
                                        />
                                        <button
                                          className="remove-image"
                                          onClick={() => removeHomepageImage(index)}
                                        >
                                          <FaTimes fill="white" />
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                  )}
                </div>

                <div className="text-container">
                <textarea
                    className="mobile-textarea homepage-textarea"
                    placeholder="Detaillierte Beschreibung für die Homepage...

Am [Datum] fand unser jährliches Sommerfest statt. Bei strahlendem Sonnenschein kamen [Anzahl] Kinder und [Anzahl] Betreuer zusammen, um einen unvergesslichen Tag zu verbringen.

Das Programm umfasste:
- Spiele und Aktivitäten
- Leckeres Essen
- Tolle Gemeinschaft

Wir bedanken uns bei allen Helfern und Teilnehmern für diesen wunderbaren Tag!"
                    value={formData.homepageDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, homepageDescription: e.target.value }))}
                    rows={12}
                />
                  <div className="character-count homepage-count">
                    {formData.homepageDescription.length} Zeichen
                  </div>
                </div>
              </div>
            </motion.div>
        );

      case 'fotoarchiv':

        return (
            <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fullscreen-step"
            >
              <div className="step-header">
                <h2>📸 Fotos auswählen</h2>
                <p>Wähle die Fotos für das Fotoarchiv aus</p>
              </div>

              <div className="fotoarchiv-content">
                {formData.fotoarchivImages.length > 0 ? (
                  <>
                    <div className="fotoarchiv-grid">
                      {formData.fotoarchivImages.map((file, index) => {
                        const stableKey = `fotoarchiv-${file.name}-${file.size}-${file.lastModified}`;
                        const uploadKey = `${file.name}-${file.size}-${file.lastModified}`;
                        const uploadStatus = uploadedImagesStatus[uploadKey] || 'pending';

                        // Verwende Thumbnail falls verfügbar, sonst zeige Platzhalter
                        const thumbnailUrl = fotoarchivThumbnails[uploadKey];

                        return (
                          <div
                            key={stableKey}
                            className={`fotoarchiv-image-item ${uploadStatus === 'uploading' ? 'uploading' : ''} ${uploadStatus === 'uploaded' ? 'uploaded' : ''} ${!thumbnailUrl ? 'loading' : ''}`}
                            onClick={() => {
                              // Für Lightbox verwende immer das Original-Bild
                              setLightboxImage(URL.createObjectURL(file));
                              setLightboxIndex(index);
                            }}
                          >
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt={`Foto ${index + 1}`}
                                loading="lazy"
                              />
                            ) : (
                              <div className="thumbnail-placeholder">
                                <ImSpinner8 className="spinning" size={24} />
                              </div>
                            )}
                            {uploadStatus === 'pending' && (
                              <button
                                className="remove-image"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFotoarchivImage(index);
                                  // Cleanup Thumbnail URL
                                  if (fotoarchivThumbnails[uploadKey]) {
                                    URL.revokeObjectURL(fotoarchivThumbnails[uploadKey]);
                                    setFotoarchivThumbnails(prev => {
                                      const newThumbs = { ...prev };
                                      delete newThumbs[uploadKey];
                                      return newThumbs;
                                    });
                                  }
                                }}
                              >
                                <FaTimes fill="white" />
                              </button>
                            )}
                            {uploadStatus === 'uploaded' && (
                              <div className="upload-success-indicator">
                                <FaCheckCircle />
                              </div>
                            )}
                          </div>
                        );
                      })}

                    </div>

                    <div
                        className="add-additional-images"
                        {...getFotoarchivRootProps()}
                    >
                      <input {...getFotoarchivInputProps()} />
                      <FaPlusCircle size={60} />
                    </div>
                  </>
                ) : (
                  <div
                    {...getFotoarchivRootProps()}
                    className={`mobile-dropzone fotoarchiv-dropzone ${isFotoarchivDragActive ? 'active' : ''}`}
                  >
                    <input {...getFotoarchivInputProps()} />
                    <FaImages size={60} />
                    <h3>Fotos hochladen</h3>
                    <p>Klicke oder ziehe Fotos hierher</p>
                  </div>
                )}
              </div>
            </motion.div>
        );


      case 'color':
        return (
          <motion.div
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fullscreen-step"
          >
            <div className="step-header">
              <h2>🎨 {selectedColor ? 'Design wählen' : 'Farbe wählen'}</h2>
            </div>

            {/* Schritt überspringen */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <button
                className="nav-button secondary"
                style={{ fontSize: '13px', padding: '6px 18px', opacity: 0.75 }}
                disabled={isLoading}
                onClick={async () => {
                  // Alten generierten Post explizit löschen
                  setCreatedPost(null);
                  setGeneratedImageBlobUrls({});

                  // Post-Daten ohne Background/Overlay aufbauen
                  const cleanedFormData = {
                    ...formData,
                    backgroundImage: null,
                    overlayText: '',
                  };

                  // UI-State zurücksetzen
                  setSelectedColor(null);
                  setVariantOptions([]);
                  setFormData(prev => ({ ...prev, backgroundImage: null, overlayText: '' }));

                  // Submit-Step-Nummer berechnen (Vorschau-Step fällt weg wenn kein Background)
                  const newSubmitStepNr = formData.backgroundImage ? totalSteps - 1 : totalSteps;

                  setIsLoading(true);
                  try {
                    const postData = {
                      ...cleanedFormData,
                      date: formatDateForBackend(cleanedFormData.date),
                      ersteller: account?.username || '',
                    };
                    console.log('🚀 Post ohne Design erstellen:', postData);
                    const result = await PostService.createPost(postData);
                    const eventData = result.event || result;
                    console.log('✅ Post erstellt:', eventData);
                    setCreatedPost(eventData);
                    setCurrentStep(newSubmitStepNr);
                  } catch (error) {
                    console.error('❌ Fehler beim Erstellen:', error);
                    alert('Fehler beim Erstellen des Posts. Bitte versuche es erneut.');
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                {isLoading ? <div className="spinner" style={{ color: 'white' }} /> : 'Diesen Schritt überspringen'}
              </button>
            </div>

            {!selectedColor ? (
              // Farbauswahl
              <div className="color-grid">
                {colorOptions.map((color) => (
                  <div
                    key={color.id}
                    className="color-option"
                    onClick={() => setSelectedColor(color.id)}
                  >
                    <div
                      className="color-preview"
                      style={{
                        backgroundImage: `url(${color.mainImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              // Variantenauswahl
              <div className="variant-container">
                <div className="variant-header">
                  <button
                    className="back-to-colors"
                    onClick={() => {
                      setSelectedColor(null);
                      setVariantOptions([]);
                      setFormData(prev => ({ ...prev, backgroundImage: null }));
                    }}
                  >
                    <FaArrowLeft />
                    Zurück zu Farben
                  </button>
                </div>

                <div className="variant-grid">
                  {variantOptions.map((variant) => (
                    <div
                      key={variant.id}
                      className={`variant-option ${formData.backgroundImage === variant.id ? 'selected' : ''}`}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, backgroundImage: variant.id }));
                        // Automatisch zum nächsten Step wechseln nach kurzer Verzögerung
                        setTimeout(() => {
                          handleNext();
                        }, 300);
                      }}
                    >
                      <div
                        className="variant-preview"
                        style={{
                          backgroundImage: `url(${variant.imageUrl})`,
                          backgroundSize: 'contain',
                          backgroundPosition: 'center'
                        }}
                      >
                        {formData.backgroundImage === variant.id && (
                          <div className="selected-indicator">
                            <FaCheckCircle />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        );

      case 'preview':
        return (
          <motion.div
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fullscreen-step preview-step"
          >
            <div className="step-header">
              <h2>📸 Vorschau</h2>
            </div>

            <div className="preview-container">
              {formData.backgroundImage ? (
                <>
                  <div
                    className="preview-background-full"
                    style={{
                      backgroundImage: `url(${variantOptions.find(v => v.id === formData.backgroundImage)?.imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <textarea
                      className="overlay-textarea"
                      placeholder="Am Donnerstagvormittag startete unser Sommerlager...."
                      value={formData.overlayText || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, overlayText: e.target.value }))}
                    />
                  </div>
                  {formData.description && (
                    <button
                      className="adopt-text-button"
                      onClick={() => {
                        const textOhneHashtags = formData.description
                          .replace(/#\S+/g, '')   // Hashtags entfernen
                          .replace(/\s+/g, ' ')   // Mehrfache Leerzeichen zusammenführen
                          .trim();
                        setFormData(prev => ({ ...prev, overlayText: textOhneHashtags }));
                      }}
                    >
                      <FaFileAlt />
                      Text von Beitrag übernehmen
                    </button>
                  )}
                </>
              ) : (
                <div className="no-background-message">
                  <div className="message-content">
                    <FaPalette size={60} color="#9d2632" />
                    <h3>Kein Hintergrund ausgewählt</h3>
                    <p>Gehe zurück zum Farbe-Step und wähle einen Hintergrund aus</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        );

      default:
        const stepIndex = steps.findIndex(step => step.title === 'Style');
        if (currentStep === stepIndex + 1) {
          return (
              <motion.div
                  variants={stepVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="fullscreen-step"
              >
                <div className="step-header">
                  <h2>🎨 Style wählen</h2>
                  <p>Wähle einen passenden Hintergrund</p>
                </div>

                <div className="background-grid">
                  {backgroundOptions.map((bg) => (
                      <div
                          key={bg.id}
                          className={`background-preview background-option ${formData.backgroundImage === bg.id ? 'selected' : ''}`}
                          style={{ background: `url(${bg.imageUrl})`, backgroundSize: 'cover' }}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, backgroundImage: bg.id }));
                            // Automatisch zum nächsten Step wechseln
                            setTimeout(() => {
                              handleNext();
                            }, 300); // Kurze Verzögerung für visuelles Feedback
                          }}
                      >

                        {formData.backgroundImage === bg.id && (
                            <div className="selected-indicator">
                              <FaCheckCircle />
                            </div>
                        )}
                      </div>
                  ))}
                </div>
              </motion.div>
          );
        }

        // Zeige ENTWEDER generierte Bilder ODER Summary
        if (createdPost && createdPost.generatedImages) {
          // ✅ Post wurde erstellt - Zeige NUR die generierten Bilder
          console.log('🎨 Zeige generierte Bilder:', createdPost.generatedImages);
          console.log('🆔 Event ID:', createdPost.id);
          console.log('📊 Anzahl Bilder:', createdPost.generatedImages.length);
          console.log('🔍 Erstes Bild:', createdPost.generatedImages[0]);

          return (
            <motion.div
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fullscreen-step generated-images-step"
            >


              <div className="generated-images-container">
                {createdPost.generatedImages
                  .sort((a, b) => a.page - b.page)
                  .map((image, index) => (
                    <div
                      key={image.page}
                      className="generated-image-card"
                      onClick={() => {
                        setLightboxImage(generatedImageBlobUrls[image.page] || null);
                        setLightboxIndex(index);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={generatedImageBlobUrls[image.page] || ''}
                        alt={`Post Seite ${image.page}`}
                        className="generated-image"
                        onError={(e) => {
                          console.error('Fehler beim Laden des Bildes:', image);
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f0f0f0" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="16" fill="%23999"%3EBild konnte nicht geladen werden%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  ))}
              </div>

            </motion.div>
          );
        }

        // ⚠️ Post wurde NOCH NICHT erstellt - Zeige Summary mit "Erstellen" Button
        console.log('📋 Zeige Summary - createdPost:', createdPost);
        return (
            <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fullscreen-step summary-step"
            >
              <div className="step-header">
                <h2>{createdPost && createdPost.generatedImages ? '🎉 Zusammenfassung' : '✨ Alles bereit!'}</h2>
                <p>{createdPost && createdPost.generatedImages ? 'Deine generierten Post-Bilder' : 'Nur noch die Bilder!'}</p>
              </div>

              <div className="summary-container">
                {/* Zeige NUR die Bilder */}
                <div className="summary-card">
                  <div className="summary-section">
                    <h4>📸 Deine Bilder</h4>
                    {(() => {
                      console.log('🔍 Summary Bilder-Check:', {
                        hasCreatedPost: !!createdPost,
                        hasGeneratedImages: !!createdPost?.generatedImages,
                        generatedImagesLength: createdPost?.generatedImages?.length,
                        generatedImages: createdPost?.generatedImages
                      });
                      return createdPost && createdPost.generatedImages && createdPost.generatedImages.length > 0;
                    })() ? (
                      // Post wurde bereits erstellt - zeige generierte Bilder vom Backend
                      (() => {
                        console.log('✅ Zeige GENERIERTE Bilder in Summary');
                        return <>
                        <div className="image-preview-grid">
                          {createdPost.generatedImages
                            .sort((a, b) => a.page - b.page)
                            .map((image, index) => (
                              <div
                                key={image.page}
                                className="preview-image-thumbnail"
                                onClick={() => {
                                  setLightboxImage(generatedImageBlobUrls[image.page] || null);
                                  setLightboxIndex(index);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={generatedImageBlobUrls[image.page] || ''}
                                  alt={`Seite ${image.page}`}
                                  style={{
                                    width: '100%',
                                    height: '100px',
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                  }}
                                  onError={(e) => {
                                    console.error('Fehler beim Laden des Bildes:', image);
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="12" fill="%23999"%3EFehler%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </div>
                            ))}
                        </div>
                        <p style={{marginTop: '10px', fontSize: '14px', color: '#666', textAlign: 'center'}}>
                          {createdPost.generatedImages.length} generierte{createdPost.generatedImages.length === 1 ? 's' : ''} Bild{createdPost.generatedImages.length === 1 ? '' : 'er'}
                        </p>
                      </>;
                      })()
                    ) : (
                      // Post wurde noch nicht erstellt - zeige hochgeladene Bilder
                      (() => {
                        console.log('⚠️ Zeige LOKALE Bilder in Summary');
                        return <>
                        <div className="image-preview-grid">
                          {formData.images.slice(0, 6).map((file, idx) => (
                            <div key={idx} className="preview-image-thumbnail">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Bild ${idx + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100px',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        {formData.images.length > 6 && (
                          <p style={{marginTop: '10px', fontSize: '14px', color: '#666'}}>
                            +{formData.images.length - 6} weitere Bilder
                          </p>
                        )}
                      </>;
                      })()
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
        );
    }
  };

  return (
      <div className="mobile-wizard">
        {/* Header */}
        <div className="wizard-header">
          {/*<div className="header-content">
          <div className="header-left">
            <div className="logo-container">
              <img
                src="/assets/logo.png"
                alt="Vereinslogo"
                className="header-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          <div className="header-right">
            <div className="profile-container">
                <NavBarProfile />
            </div>
          </div>
        </div>*/}

          <div className="progress-container">
            <div
                className="progress-bar"
                style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <div className="step-dots">
            {steps.map((step) => (
                <div
                    key={step.number}
                    className={`step-dot ${currentStep >= step.number ? 'active' : ''} ${
                        step.number <= currentStep || (step.number === 2 && formData.platforms.length > 0)
                            ? 'clickable'
                            : 'disabled'
                    }`}
                    onClick={() => handleStepClick(step.number)}
                >
                  <span>{step.title}</span>
                </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="wizard-content">
          <AnimatePresence mode="wait">
            {renderStepContent()}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        {currentStep !== 1 && (
        <div className="wizard-navigation">
          <button
              className="nav-button secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1}
          >
            <FaArrowLeft />
            Zurück
          </button>

          {currentStep < totalSteps || !createdPost ? (
              // Prüfe ob wir beim Fotoarchiv-Foto-Step sind
              (() => {
                const currentStepData = steps[currentStep - 1];
                const isFotoarchivPhotoStep = currentStepData?.type === 'fotoarchiv';

                // Fotoarchiv-Foto-Step: Zeige grünen Fertig-Button
                if (isFotoarchivPhotoStep) {
                  return (
                    <button
                      className="nav-button success"
                      onClick={async () => {
                        if (!formData.fotoarchivAktionId) {
                          console.error('❌ Keine Fotoarchiv-Aktion-ID vorhanden!');
                          alert('Fehler: Keine Aktion gefunden. Bitte versuche es erneut.');
                          return;
                        }

                        if (formData.fotoarchivImages.length === 0) {
                          alert('Bitte wähle mindestens ein Foto aus.');
                          return;
                        }

                        try {
                          setIsLoading(true);

                          // Setze alle Bilder initial auf "uploading"
                          const initialStatus = {};
                          formData.fotoarchivImages.forEach((file, index) => {
                            const key = `${file.name}-${file.size}-${file.lastModified}`;
                            initialStatus[key] = 'uploading';
                          });
                          setUploadedImagesStatus(initialStatus);

                          console.log('📤 Lade Fotos hoch zu Aktion', formData.fotoarchivAktionId);

                          const result = await FotoarchivService.uploadPhotosWithProgress(
                            formData.fotoarchivAktionId,
                            formData.fotoarchivImages,
                            (index, file) => {
                              // Callback: Bild wurde hochgeladen
                              const key = `${file.name}-${file.size}-${file.lastModified}`;
                              setUploadedImagesStatus(prev => ({
                                ...prev,
                                [key]: 'uploaded'
                              }));
                            }
                          );

                          console.log('✅ Upload erfolgreich:', result);

                          if (result.success) {
                            // Aktion abschließen
                            try {
                              await FotoarchivService.fertig(formData.fotoarchivAktionId);
                              console.log('✅ Fotoarchiv-Aktion abgeschlossen');
                            } catch (fertigError) {
                              console.error('❌ Fehler beim Abschließen der Aktion:', fertigError);
                              // Nicht blocken - Upload war erfolgreich
                            }

                            // Zeige Erfolgs-Overlay
                            setShowSuccessOverlay(true);

                            // Nach 3 Sekunden zurücksetzen und zur Hauptseite
                            setTimeout(() => {
                              console.log('🔄 Setze Wizard zurück...');
                              setCurrentStep(1);
                              setUploadedImagesStatus({});
                              setFormData({
                                platforms: [],
                                title: '',
                                date: new Date().toISOString().split('T')[0],
                                location: '',
                                leaders: 0,
                                children: 0,
                                images: [],
                                homepageImages: [],
                                description: '',
                                homepageDescription: '',
                                backgroundImage: null,
                                fotoarchivName: '',
                                fotoarchivDatum: new Date().toISOString().split('T')[0],
                                fotoarchivOrt: '',
                                fotoarchivImages: [],
                                fotoarchivAktionId: null
                              });
                              window.location.href = '/';
                            }, 3000);
                          }
                        } catch (error) {
                          console.error('❌ Fehler beim Upload:', error);
                          alert('Fehler beim Hochladen der Fotos. Bitte versuche es erneut.');
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={formData.fotoarchivImages.length === 0 || isLoading}
                    >
                      {isLoading ? <div className="spinner" style={{ color: 'white' }} /> : <>Fertig <FaCheckCircle /></>}
                    </button>
                  );
                }

                // Normaler Weiter-Button
                return (
                  <button
                      className="nav-button primary"
                      onClick={async () => {
                        // Prüfe ob der NÄCHSTE Step der Submit-Step ist
                        const currentStepData = steps[currentStep - 1];
                        const nextStepData = steps[currentStep]; // Nächster Step (currentStep + 1 - 1)
                        const isBeforeSubmit = nextStepData?.type === 'submit' || nextStepData?.title === 'Erstellen';

                        console.log('🔍 Weiter-Button geklickt:', {
                          currentStep,
                          totalSteps,
                          currentStepData,
                          nextStepData,
                          isBeforeSubmit,
                          hasCreatedPost: !!createdPost
                        });

                        if (isBeforeSubmit && !createdPost) {
                          // Nächster Step ist Submit-Step und Post noch nicht erstellt
                          // Erstelle Post JETZT, bevor wir zum Submit-Step wechseln
                          console.log('✅ Erstelle Post jetzt (vor Wechsel zum Submit-Step)!');
                          setIsLoading(true);
                          try {
                            // Konvertiere Datum zu TT.MM.JJJJ Format
                            const postDataWithFormattedDate = {
                              ...formData,
                              date: formatDateForBackend(formData.date),
                              ersteller: account?.username || '' // Füge Ersteller hinzu
                            };
                            console.log('🚀 Post erstellen:', postDataWithFormattedDate);
                            const result = await PostService.createPost(postDataWithFormattedDate);
                            console.log('✅ Post erfolgreich erstellt:', result);

                            const eventData = result.event || result;
                            console.log('📸 Event Daten:', eventData);
                            console.log('📸 Generierte Bilder:', eventData.generatedImages);
                            console.log('🆔 Event ID:', eventData.id);

                            setCreatedPost(eventData);
                            console.log('💾 createdPost gesetzt:', eventData);

                            // Wechsel zum nächsten Step (Zusammenfassung mit generierten Bildern)
                            console.log('➡️ Wechsel zu Zusammenfassung-Step');
                            handleNext();
                          } catch (error) {
                            console.error('❌ Fehler beim Erstellen des Posts:', error);
                          } finally {
                            setIsLoading(false);
                          }
                        } else {
                          // Normaler Step-Wechsel
                          console.log('⚠️ Kein Post-Erstellen, normaler Wechsel. Grund:', {
                            isBeforeSubmit,
                            hasCreatedPost: !!createdPost,
                            reason: !isBeforeSubmit ? 'Nächster Step ist nicht Submit-Step' : 'Post bereits erstellt'
                          });
                          handleNext();
                        }
                      }}
                      disabled={
                        (currentStep === 1 && formData.platforms.length === 0) ||
                        (currentStep === 2 && formData.platforms.includes('fotoarchiv') && (!formData.fotoarchivName || !formData.fotoarchivDatum || !formData.fotoarchivOrt)) ||
                        (currentStep === 2 && !formData.platforms.includes('fotoarchiv') && (!formData.title || !formData.date || !formData.location)) ||
                        (currentStepData?.type === 'instagram' && (formData.images.length === 0 || !formData.description.trim())) ||
                        (currentStepData?.type === 'preview' && !formData.overlayText?.trim()) ||
                        isLoading
                      }
                  >
                    {isLoading ? <div className="spinner" style={{ color: 'white' }} /> : <>Weiter <FaArrowRight /></>}

                  </button>
                );
              })()
          ) : (
              // Fertig-Button (wenn letzter Step und Post erstellt)
              <button
                  className="nav-button success"
                  onClick={async () => {
                    try {
                      console.log('✅ Fertig - Post wurde erfolgreich erstellt!');

                      // 1. Setze Post-Status auf "bereit"
                      if (createdPost && createdPost.id) {
                        console.log('🎯 Setze Status auf "bereit"...');
                        await PostService.markAsReady(createdPost.id);
                      }

                      // 2. Zeige Erfolgs-Overlay
                      setShowSuccessOverlay(true);

                      // 3. Warte 2 Sekunden, dann zurück zur Hauptseite
                      setTimeout(() => {
                        console.log('🔄 Setze Wizard zurück...');
                        setCurrentStep(1);
                        setCreatedPost(null);
                        setSelectedColor(null);
                        setVariantOptions([]);
                        setFormData({
                          platforms: [],
                          title: '',
                          date: new Date().toISOString().split('T')[0],
                          location: '',
                          leaders: 0,
                          children: 0,
                          images: [],
                          homepageImages: [],
                          description: '',
                          homepageDescription: '',
                          backgroundImage: null,
                          fotoarchivName: '',
                          fotoarchivDatum: new Date().toISOString().split('T')[0],
                          fotoarchivOrt: '',
                          fotoarchivImages: [],
                          fotoarchivAktionId: null
                        });

                        console.log('🏠 Navigiere zur Hauptseite...');
                        window.location.href = '/';
                      }, 3000);

                    } catch (error) {
                      console.error('❌ Fehler beim Abschließen:', error);
                      alert('Fehler beim Abschließen des Posts. Bitte versuche es erneut.');
                    }
                  }}
              >
                Fertig
                <FaCheckCircle />
              </button>
          )}
        </div>
        )}

        {/* Detail-Modal für letzte Beiträge */}
        {selectedRecentPost && !postDetailLightbox && (
          <div className="post-detail-overlay" onClick={() => setSelectedRecentPost(null)}>
            <div className="post-detail-modal" onClick={(e) => e.stopPropagation()}>
              {/* Schließen-Button */}
              <button className="post-detail-close" onClick={() => setSelectedRecentPost(null)}>
                <FaTimes />
              </button>

              {/* Titel + Meta */}
              <div className="post-detail-header">
                <h2>{selectedRecentPost.title}</h2>
                <div className="post-detail-meta">
                  <span><FaCalendarAlt /> {selectedRecentPost.date}</span>
                  {selectedRecentPost.location && <span><FaMapMarkerAlt /> {selectedRecentPost.location}</span>}
                  {selectedRecentPost.leaders > 0 && <span><FaUsers /> {selectedRecentPost.leaders} Leiter</span>}
                  {selectedRecentPost.children > 0 && <span><FaChild /> {selectedRecentPost.children} Kinder</span>}
                </div>
                {selectedRecentPost.instagramDescription && (
                  <p className="post-detail-description">{selectedRecentPost.instagramDescription}</p>
                )}
              </div>

              {/* Bilder horizontal scrollbar im 4:5 Format */}
              <div className="post-detail-images-full">
                {(selectedRecentPost.generatedImages || [])
                  .sort((a, b) => a.page - b.page)
                  .map((img, idx) => (
                    <div
                      key={img.page}
                      className="post-detail-image-full-wrap"
                      onClick={() => {
                        setPostDetailLightboxIndex(idx);
                        setPostDetailLightbox(selectedPostAllImages[img.page] || null);
                      }}
                    >
                      {selectedPostAllImages[img.page] ? (
                        <>
                          <img
                            src={selectedPostAllImages[img.page]}
                            alt={`Seite ${img.page}`}
                            className="post-detail-image-full"
                          />

                          <span className="post-detail-page-badge">{img.page}</span>
                        </>
                      ) : (
                        <div className="post-detail-image-loading-full">
                          <ImSpinner8 className="spin" size={28} />
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Lightbox für Detail-Bilder */}
        {selectedRecentPost && postDetailLightbox && (
          <div className="lightbox-overlay" onClick={() => setPostDetailLightbox(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" onClick={() => setPostDetailLightbox(null)}>
                <FaTimes />
              </button>
              <img src={postDetailLightbox} alt="Vorschau" className="lightbox-image" />
              {selectedRecentPost.generatedImages && selectedRecentPost.generatedImages.length > 1 && (
                <>
                  <button
                    className="lightbox-nav lightbox-prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      const sorted = [...selectedRecentPost.generatedImages].sort((a, b) => a.page - b.page);
                      const newIdx = postDetailLightboxIndex > 0 ? postDetailLightboxIndex - 1 : sorted.length - 1;
                      setPostDetailLightboxIndex(newIdx);
                      setPostDetailLightbox(selectedPostAllImages[sorted[newIdx].page] || null);
                    }}
                  >
                    <FaArrowLeft />
                  </button>
                  <button
                    className="lightbox-nav lightbox-next"
                    onClick={(e) => {
                      e.stopPropagation();
                      const sorted = [...selectedRecentPost.generatedImages].sort((a, b) => a.page - b.page);
                      const newIdx = postDetailLightboxIndex < sorted.length - 1 ? postDetailLightboxIndex + 1 : 0;
                      setPostDetailLightboxIndex(newIdx);
                      setPostDetailLightbox(selectedPostAllImages[sorted[newIdx].page] || null);
                    }}
                  >
                    <FaArrowRight />
                  </button>
                  <div className="lightbox-counter">
                    {postDetailLightboxIndex + 1} / {selectedRecentPost.generatedImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Lightbox für Bildvorschau */}
        {lightboxImage && (
          <div
            className="lightbox-overlay"
            onClick={() => setLightboxImage(null)}
          >
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="lightbox-close"
                onClick={() => setLightboxImage(null)}
              >
                <FaTimes />
              </button>

              <img
                src={lightboxImage}
                alt="Vorschau"
                className="lightbox-image"
              />

              {createdPost && createdPost.generatedImages && createdPost.generatedImages.length > 1 && (
                <>
                  <button
                    className="lightbox-nav lightbox-prev"
                    onClick={() => {
                      const newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : createdPost.generatedImages.length - 1;
                      setLightboxIndex(newIndex);
                      const sortedImages = [...createdPost.generatedImages].sort((a, b) => a.page - b.page);
                      setLightboxImage(generatedImageBlobUrls[sortedImages[newIndex].page] || null);
                    }}
                  >
                    <FaArrowLeft />
                  </button>

                  <button
                    className="lightbox-nav lightbox-next"
                    onClick={() => {
                      const newIndex = lightboxIndex < createdPost.generatedImages.length - 1 ? lightboxIndex + 1 : 0;
                      setLightboxIndex(newIndex);
                      const sortedImages = [...createdPost.generatedImages].sort((a, b) => a.page - b.page);
                      setLightboxImage(generatedImageBlobUrls[sortedImages[newIndex].page] || null);
                    }}
                  >
                    <FaArrowRight />
                  </button>

                  <div className="lightbox-counter">
                    {lightboxIndex + 1} / {createdPost.generatedImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Erfolgs-Overlay */}
        {showSuccessOverlay && formData.platforms.length > 0 && (
          <motion.div
            className="success-overlay"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="success-content">
              <motion.div
                className="success-checkmark"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <FaCheckCircle size={80} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {formData.platforms.includes('fotoarchiv')
                  ? 'Danke für die Fotos!'
                  : formData.platforms.includes('instagram')
                  ? 'Danke für deinen Post!'
                  : 'Danke!'}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {formData.platforms.includes('fotoarchiv')
                  ? 'Deine Fotos wurden erfolgreich hochgeladen'
                  : formData.platforms.includes('instagram')
                  ? 'Dein Beitrag wurde erfolgreich erstellt'
                  : 'Erfolgreich erstellt'}
              </motion.p>
            </div>
          </motion.div>
        )}
      </div>
  );
};

export default Wizard;
